import { useState } from "react";
import { Lock, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const SeatEditor = ({ liveId, chamber, p, testid, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [party, setParty] = useState("");
  const [won, setWon] = useState(0);
  const [leading, setLeading] = useState("");
  const [lock, setLock] = useState(false);
  const toggle = (o) => {
    if (o) { setParty(p?.party || ""); setWon(p?.won ?? 0); setLeading(p?.leading ?? ""); setLock(!!p?.locked); }
    setOpen(o);
  };
  const post = async (path, body, ok) => {
    try { const r = await api.post(`/admin/live/${liveId}/country/results/${path}`, body); toast.success(ok); setOpen(false); onSaved(r.data.results_board); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const save = () => post("seat", { chamber, party, won: Number(won) || 0, leading: leading === "" ? null : Number(leading), lock }, lock ? "Seats corrected and locked" : "Seats corrected");
  return (
    <Popover open={open} onOpenChange={toggle}>
      <PopoverTrigger asChild>
        {p ? <button type="button" className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label={`Correct ${p.party}`} data-testid={`${testid}-edit`}><Pencil size={12} /></button>
          : <Button size="sm" variant="ghost" className="mt-1 h-7 rounded-full px-2 text-xs" data-testid={`${testid}-add`}><Plus size={12} className="mr-1" />Add party</Button>}
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" data-testid={`${testid}-editor`}>
        <p className="text-sm font-semibold">{p ? `Correct ${p.party}` : "Add a party"} · {chamber}</p>
        {!p && <label className="block text-xs text-slate-500">Party<Input value={party} onChange={(e) => setParty(e.target.value)} className="mt-1 text-slate-900" data-testid={`${testid}-party-input`} /></label>}
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-slate-500">Won<Input type="number" min="0" value={won} onChange={(e) => setWon(e.target.value)} className="mt-1 text-slate-900" data-testid={`${testid}-won-input`} /></label>
          <label className="block text-xs text-slate-500">Leading<Input type="number" min="0" value={leading} onChange={(e) => setLeading(e.target.value)} placeholder="—" className="mt-1 text-slate-900" data-testid={`${testid}-leading-input`} /></label>
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-600"><Checkbox checked={lock} onCheckedChange={(v) => setLock(!!v)} className="mt-0.5" data-testid={`${testid}-lock-checkbox`} />
          <span><b>Lock these numbers.</b> Unlocked corrections may be replaced by the next 15-minute update; locked ones stay until you unlock them.</span></label>
        <div className="flex gap-2">
          <Button size="sm" className="rounded-full" disabled={!party.trim()} onClick={save} data-testid={`${testid}-save`}>Save</Button>
          {p?.locked && <Button size="sm" variant="outline" className="rounded-full" onClick={() => post("unlock", { chamber, party: p.party }, "Unlocked. The next update can change it")} data-testid={`${testid}-unlock`}><Lock size={12} className="mr-1" />Unlock</Button>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
